import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "reactstrap";
import Widget from "./Widgets";
import Revenue from "./Revenue";
import Section from "./Section";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import { Indonesian } from "flatpickr/dist/l10n/id.js";
import "./fileCss.css";
import { Link } from "react-router-dom";
import { adminGetAllEvents } from "helpers/backend_helpers";

interface EventCalendar {
  date: string;
  title: string;
}

interface EventDetail {
  _id: string;
  createTime: string;
  creatorId: string;
  updateTime: string | null;
  updaterId: string | null;
  name: string;
  location: string;
  eventDatetime: string;
  note: string;
}

const DashboardEcommerce = () => {
  const [events, setEvents] = useState<EventCalendar[]>([]);
  const [eventList, setEventList] = useState<EventDetail[]>([]);
  const [sort, setSort] = useState("createTime");
  const [dir, setDir] = useState(-1);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  const getCurrentMonthYear = () => {
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      year: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const filterEventsByCurrentMonth = (events: EventDetail[]) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return events.filter((event) => {
      const eventDate = new Date(event.eventDatetime);
      return (
        eventDate.getMonth() === currentMonth &&
        eventDate.getFullYear() === currentYear
      );
    });
  };

  const getDataEvent = async () => {
    let query: any = {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sort,
      dir: dir,
    };
    adminGetAllEvents(query)
      .then((response: any) => {
        const transformedEvents: EventCalendar[] = response.content.map(
          (item: any) => ({
            date: item.eventDatetime.split("T")[0],
            title: item.name,
          }),
        );
        setEvents(transformedEvents);

        const filteredEvents = filterEventsByCurrentMonth(response.content);
        setEventList(filteredEvents);
      })
      .catch(() => {})
      .finally(() => {});
  };

  useEffect(() => {
    getDataEvent();
  }, [pagination.pageIndex, pagination.pageSize, sort, dir]);

  const formatDateIndonesia = (datetime: string) => {
    const date = new Date(datetime);
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  document.title = "Dashboard";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col>
              <div className="h-100">
                <Section />
                <Row>
                  <Col xxl={8}>
                    <Widget />
                    <Revenue />
                  </Col>
                  <div className="col-xxl-4">
                    <div className="card">
                      <div className="card-header border-0 d-flex justify-content-between align-items-center">
                        <h4 className="card-title mb-0">Event</h4>
                        <span
                          className="text-muted"
                          style={{ fontSize: "0.875rem" }}
                        >
                          {getCurrentMonthYear()}
                        </span>
                      </div>
                      <div className="card-body pt-0">
                        <div className="upcoming-scheduled">
                          <Flatpickr
                            className="form-control"
                            options={{
                              dateFormat: "d F Y",
                              locale: Indonesian,
                              inline: true,
                              defaultDate: "today",
                              onDayCreate: (dObj, dStr, fp, dayElem) => {
                                const date = [
                                  dayElem.dateObj.getFullYear(),
                                  String(
                                    dayElem.dateObj.getMonth() + 1,
                                  ).padStart(2, "0"),
                                  String(dayElem.dateObj.getDate()).padStart(
                                    2,
                                    "0",
                                  ),
                                ].join("-");

                                const event = events.find(
                                  (e: EventCalendar) => e.date === date,
                                );
                                if (event) {
                                  dayElem.classList.add("has-event");
                                  dayElem.setAttribute("title", event.title);
                                }
                              },
                            }}
                          />
                        </div>

                        <h6 className="text-uppercase fw-semibold mt-4 mb-3 text-muted">
                          Daftar Event:
                        </h6>

                        {eventList.length > 0 ? (
                          eventList.map((event: EventDetail, index: number) => {
                            const eventDate = new Date(event.eventDatetime);
                            const day = eventDate
                              .getDate()
                              .toString()
                              .padStart(2, "0");
                            const hours24 = eventDate
                              .getHours()
                              .toString()
                              .padStart(2, "0");
                            const minutes = eventDate
                              .getMinutes()
                              .toString()
                              .padStart(2, "0");

                            return (
                              <div key={event._id || index}>
                                <div className="mini-stats-wid d-flex align-items-center mt-3">
                                  <div className="flex-shrink-0 avatar-sm">
                                    <span className="mini-stat-icon avatar-title rounded-circle text-success bg-success-subtle fs-4">
                                      {day}
                                    </span>
                                  </div>
                                  <div className="flex-grow-1 ms-3">
                                    <h6 className="mb-1">{event.name}</h6>
                                    <p className="text-muted mb-0">
                                      {event.location}
                                    </p>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <p className="text-muted mb-0">
                                      {hours24}:{minutes}
                                    </p>
                                  </div>
                                </div>
                                <p
                                  className="text-muted mb-0 ms-5 ps-2"
                                  style={{ fontSize: "0.75rem" }}
                                >
                                  {formatDateIndonesia(event.eventDatetime)}
                                </p>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted text-center">
                            Tidak ada event pada bulan ini
                          </p>
                        )}

                        <div className="mt-3 text-center">
                          <Link
                            to="/event"
                            className="text-muted text-decoration-underline"
                          >
                            Selengkapnya
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DashboardEcommerce;
